import { useCallback, useEffect, useState } from 'react';
import { ScanLine, PackagePlus } from 'lucide-react';
import { api, ErrorApi } from '../api';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import Modal from '../ui/Modal';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { motivoDeError } from '../ui/estados';
import { nombreDelTipo } from '../accesos/codigos';

/* =====================================================================
   CODIGOS POR RESOLVER · Accesos Fase 4
   =====================================================================
   NO ES UNA BANDEJA DE ERRORES, Y EL SUBTITULO LO DICE. Es información
   de negocio: qué está entrando al almacén sin estar dado de alta.

   Antes, escanear algo que TEGO no conocía era un «no encontrado» y se
   acabó — la información de que ese código existe y llegó a esa puerta
   se evaporaba, aunque quien lo escaneó ni siquiera tuviera permiso para
   dar de alta productos.

   ORDENADO POR CUANTAS VECES SE HA VISTO, no por fecha. Lo que se ha
   escaneado cuarenta veces es una caja que entra a diario y que hay que
   dar de alta ya; lo que se vio una vez puede ser un lector que leyó
   mal.
   ===================================================================== */
export default function CodigosPorResolver({ irA }) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [asignando, setAsignando] = useState(null);

  // U-03 fase a · «Crear un producto con este código»: abre «Dar de alta
  // desde el código» con el código y su fila de la cola; esa pantalla crea
  // el producto y luego lo asigna con la misma ruta `…/asignar` del modal.
  const crearCon = (c) => {
    setAsignando(null);
    irA('gtin-alta', { codigo: c.valor, id_codigo: c.id });
  };

  const cargar = useCallback(() => api.get('/qr/codigos-por-resolver')
    .then((d) => { setFilas(d); setError(null); })
    .catch((e) => setError(e.message)), []);

  useEffect(() => {
    let vigente = true;
    api.get('/qr/codigos-por-resolver')
      .then((d) => { if (vigente) { setFilas(d); setError(null); } })
      .catch((e) => vigente && setError(e.message))
      .finally(() => vigente && setCargando(false));
    return () => { vigente = false; };
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Códigos que TEGO no reconoció</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Lo que tu gente ha escaneado y TEGO no supo qué era. Dile de qué
          producto es cada uno y a partir de ahí se resuelve solo.
        </p>
      </div>

      <NotaExplicativa titulo="¿De dónde sale esta lista?">
        Cuando alguien escanea un código que TEGO no conoce —la caja de un
        proveedor nuevo, por ejemplo— queda apuntado aquí en vez de
        perderse. <strong>No hace falta que esa persona tenga permiso para
        dar de alta productos</strong>: solo escanea y sigue trabajando.
        {' '}Lo que ves ordenado arriba es lo que más veces se ha
        escaneado.
      </NotaExplicativa>

      {/* Los tres estados no se pisan: el error NO se enseña a la vez que
          «Nada por resolver» (eso hacía ver un tropiezo de red como una
          lista limpia). El error trae su motivo en español y reintentar. */}
      {error && !cargando && (
        <ErrorConReintento
          que="No se pudo cargar la lista de códigos"
          porque={motivoDeError(error)}
          onReintentar={() => { setError(null); setCargando(true); cargar().finally(() => setCargando(false)); }}
        />
      )}
      {cargando && <Cargando filas={4} />}

      {!cargando && !error && filas.length === 0 && (
        <EstadoVacio
          icono={ScanLine}
          titulo="Nada por resolver"
          descripcion="Todo lo que se ha escaneado en tu almacén, TEGO lo reconoció. Cuando alguien lea un código nuevo, aparecerá aquí."
        />
      )}

      {filas.length > 0 && (
        <ListaEstandar
          titulo="Sin asignar"
          subtitulo="Toca uno para decirle a TEGO de qué producto es."
          columnas={[
            { etiqueta: 'Código', valor: (c) => c.valor, clase: 'font-mono text-gray-900' },
            // U-02 fase c: el nombre humano (GTIN-14, Code 128), no el de máquina.
            { etiqueta: 'Simbología', valor: (c) => (c.tipo ? nombreDelTipo(c.tipo) : '—') },
            {
              etiqueta: 'Veces', valor: (c) => c.veces,
              render: (c) => (
                <span className={c.veces >= 5 ? 'font-semibold text-amber-700' : ''}>
                  {c.veces}
                </span>
              ),
            },
            { etiqueta: 'Lo vio', valor: (c) => c.visto_por || '—' },
            {
              etiqueta: 'Última vez', tipo: 'fecha_hora', valor: (c) => c.ultima_vez_en,
              render: (c) => <TiempoRelativo valor={c.ultima_vez_en} />,
            },
          ]}
          filas={filas}
          exportar={{ nombre: 'codigos-por-resolver' }}
          claveFila={(c) => c.id}
          // CORRECCIÓN (U-03 fase a): `ListaEstandar` no conoce `alTocar`,
          // así que tocar un renglón no abría nada y el modal de asignar no
          // tenía entrada desde que existe (Accesos Fase 4). Se quita esa
          // prop muerta (auditoría U-03 a, H7) y con ella la excepción de
          // `pruebas/autorizacionesAbrir.test.js`. Las acciones
          // del renglón sí las pinta: «Asignar a un producto» abre ese
          // modal, y «Crear un producto con este código» abre el alta con
          // el código y su fila de la cola.
          acciones={(c) => [
            { etiqueta: 'Asignar a un producto', onClick: () => setAsignando(c) },
            ...(irA ? [{ etiqueta: 'Crear un producto con este código', onClick: () => crearCon(c) }] : []),
          ]}
        />
      )}

      {asignando && (
        <ModalAsignar
          codigo={asignando}
          onCrear={irA ? () => crearCon(asignando) : null}
          onCerrar={() => setAsignando(null)}
          onAsignado={() => { setAsignando(null); cargar(); }}
        />
      )}
    </div>
  );
}


function ModalAsignar({ codigo, onCerrar, onAsignado, onCrear }) {
  const [busqueda, setBusqueda] = useState('');
  const [productos, setProductos] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(null);

  useEffect(() => {
    // EL `setProductos([])` DEL CASO CORTO SE HACE EN EL TEMPORIZADOR y
    // no aquí: escribir estado de forma síncrona dentro de un efecto
    // encadena un render de más en cada tecla, y esto se dispara al
    // escribir. Con el retardo compartido, el camino es uno solo.
    let vigente = true;
    const t = setTimeout(() => {
      if (busqueda.trim().length < 2) {
        if (vigente) setProductos([]);
        return;
      }
      api.get(`/productos?texto=${encodeURIComponent(busqueda.trim())}&limite=15`)
        .then((d) => vigente && setProductos(Array.isArray(d) ? d : (d.datos || [])))
        .catch(() => vigente && setProductos([]));
    }, 250);
    return () => { vigente = false; clearTimeout(t); };
  }, [busqueda]);

  async function asignar() {
    if (!elegido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post(
        `/qr/codigos-por-resolver/${codigo.id}/asignar`,
        { id_producto: elegido.id });
      setHecho(r.mensaje);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo asignar.');
      setEnviando(false);
    }
  }

  if (hecho) {
    return (
      <Modal titulo="Listo" onCerrar={onAsignado}>
        <div className="space-y-4">
          <p className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded p-3">
            {hecho}
          </p>
          <button type="button" onClick={onAsignado}
                  className="w-full bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm">
            Entendido
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titulo={`Código ${codigo.valor}`} onCerrar={onCerrar}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Se ha escaneado <strong>{codigo.veces}</strong> vez
          {codigo.veces === 1 ? '' : 'es'}. Dinos de qué producto es y a
          partir de ahora escanearlo lo abrirá directamente.
        </p>

        <div>
          <label htmlFor="buscar-prod" className="block text-sm font-semibold text-gray-900 mb-1">
            ¿De qué producto es?
          </label>
          <input
            id="buscar-prod" type="search" value={busqueda} autoFocus
            placeholder="Escribe el nombre o el SKU…"
            onChange={(e) => { setBusqueda(e.target.value); setElegido(null); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          {/* SE BUSCA, NO SE ELIGE DE UNA LISTA DE DOS MIL. Un desplegable
              con el catálogo entero es inservible en un almacén de verdad. */}
          {productos.length > 0 && (
            <ul className="mt-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {productos.map((p) => (
                <li key={p.id}>
                  <button
                    type="button" onClick={() => setElegido(p)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                      elegido?.id === p.id ? 'bg-blue-50 font-semibold' : ''}`}
                  >
                    {p.nombre}
                    <span className="ml-2 font-mono text-xs text-gray-500">{p.sku}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">
            {error}
          </div>
        )}

        <button
          type="button" onClick={asignar} disabled={!elegido || enviando}
          title={!elegido ? 'Elige primero el producto de la lista.' : undefined}
          className="w-full bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg text-sm"
        >
          {enviando ? 'Asignando…'
            : elegido ? `Este código es de ${elegido.nombre}` : 'Elige un producto'}
        </button>

        {/* U-03 fase a · para un código cuyo producto no está dado de alta. */}
        {onCrear && (
          <button
            type="button" onClick={onCrear}
            className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold py-2.5 rounded-lg text-sm"
          >
            <PackagePlus className="h-4 w-4" aria-hidden="true" /> Crear un producto con este código
          </button>
        )}
      </div>
    </Modal>
  );
}
